import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import NewsletterSignUp from '../../common/Newsletter-sign';
import Footer from '../../Footer/Footer';
import festive from '../../../assets/christmas25/festive.webp';
import grotto from '../../../assets/christmas25/grotto.webp';
import search from '../../../assets/christmas25/search.webp';
import tea from '../../../assets/christmas25/tea2.webp';
import { useBlackFriday } from '../../../contexts/BlackFridayContext';

const BOOKING_URL = '/booking-journey?locationId=978&pageId=f7be8040-b3de-43e8-a064-b173b1a8d761';

const C = {
  navy:     '#101d40',
  midnight: '#0A1428',
  gold:     '#C9963C',
  goldDk:   '#A67C2A',
  ivory:    '#FAF7F0',
  parchment:'#F3EDD8',
  muted:    'rgba(28,28,30,0.62)',
  border:   'rgba(28,28,30,0.08)',
};

/* ── PAGE ── */
const Page = styled.div`
  min-height: 100vh;
  background: ${C.ivory};
  padding-top: 158px;
  @media (max-width: 768px) { padding-top: clamp(188px, 34vw, 228px); }
`;

/* ── HERO ── */
const HeroWrap = styled.section`
  position: relative;
  width: 100%;
  height: clamp(420px, 62vh, 680px);
  overflow: hidden;
  display: flex;
  align-items: flex-end;
  @media (max-width: 768px) { height: clamp(460px, 72svh, 600px); }
`;

const HeroImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 35%;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to top,
    rgba(16,29,64,0.94) 0%,
    rgba(16,29,64,0.62) 32%,
    rgba(16,29,64,0.18) 60%,
    transparent 100%
  );
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: clamp(2rem, 5vw, 3.5rem) clamp(1.25rem, 5vw, 3.5rem);
  color: #fff;
`;

const HeroKicker = styled.p`
  margin: 0 0 0.65rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${C.gold};
`;

const HeroTitle = styled.h1`
  margin: 0 0 0.65rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(2.4rem, 6vw, 4.4rem);
  font-weight: 700;
  line-height: 1.05;
  color: #fff;
  em { font-style: italic; color: ${C.gold}; }
`;

const HeroStrap = styled.p`
  margin: 0 0 1.75rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1rem, 2.2vw, 1.35rem);
  font-style: italic;
  font-weight: 300;
  color: rgba(255,255,255,0.82);
  max-width: 36rem;
`;

const HeroBtnRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
`;

const HeroPrimaryBtn = styled.a`
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  padding: 1rem 2.25rem;
  border-radius: 999px;
  background: ${C.gold};
  color: #fff;
  text-decoration: none;
  transition: background 0.2s ease, transform 0.18s ease;
  &:hover { background: ${C.goldDk}; transform: translateY(-1px); }
`;

const HeroSecondaryBtn = styled(Link)`
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 1rem 2rem;
  border-radius: 999px;
  border: 1.5px solid rgba(255,255,255,0.42);
  background: transparent;
  color: #fff;
  text-decoration: none;
  transition: border-color 0.2s ease, background 0.2s ease;
  &:hover { border-color: rgba(255,255,255,0.65); background: rgba(255,255,255,0.06); }
`;

/* ── INTRO STRIP (light buffer after hero) ── */
const IntroStrip = styled.section`
  padding: clamp(3rem, 6vw, 4.5rem) clamp(1.25rem, 5vw, 3.5rem);
  background: ${C.ivory};
  border-bottom: 1px solid ${C.border};
`;

const IntroStripInner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: clamp(2rem, 5vw, 4rem);
  align-items: center;

  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const IntroStripLead = styled.p`
  margin: 0 0 clamp(1.25rem, 2.5vw, 1.75rem);
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: clamp(0.95rem, 1.8vw, 1.05rem);
  line-height: 1.7;
  color: ${C.muted};
  max-width: 34rem;
`;

const IntroQuickGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1px;
  background: ${C.border};
  border-radius: 10px;
  overflow: hidden;

  @media (max-width: 560px) { grid-template-columns: 1fr; }
`;

const IntroQuickItem = styled.div`
  background: #fff;
  padding: clamp(1.1rem, 2.5vw, 1.45rem);
`;

const IntroQuickLabel = styled.div`
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(16,29,64,0.38);
  margin-bottom: 0.35rem;
`;

const IntroQuickValue = styled.div`
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(0.95rem, 2vw, 1.1rem);
  font-weight: 700;
  color: ${C.navy};
  line-height: 1.25;
`;

const IntroStripLink = styled(Link)`
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${C.gold};
  text-decoration: none;
  transition: color 0.2s ease;
  &:hover { color: ${C.goldDk}; }
`;

/* ── SHARED SECTION ── */
const Section = styled.section`
  padding: clamp(3.5rem, 7vw, 5.5rem) clamp(1.25rem, 5vw, 3.5rem);
  background: ${p => p.$bg || 'transparent'};
  border-bottom: ${p => p.$border ? `1px solid ${C.border}` : 'none'};
`;

const Inner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

const Kicker = styled.p`
  margin: 0 0 0.6rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${C.gold};
  text-align: ${p => p.$center ? 'center' : 'left'};
`;

const Divider = styled.div`
  width: 48px;
  height: 2px;
  background: ${C.gold};
  margin: ${p => p.$center ? '0 auto clamp(1.1rem, 2.5vw, 1.6rem)' : '0 0 clamp(1.1rem, 2.5vw, 1.6rem)'};
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.75rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.8rem, 4vw, 2.75rem);
  font-weight: 700;
  line-height: 1.1;
  color: ${p => p.$light ? '#fff' : C.navy};
  text-align: ${p => p.$center ? 'center' : 'left'};
  em { font-style: italic; color: ${C.gold}; }
`;

const SectionLead = styled.p`
  max-width: ${p => p.$wide ? '760px' : '640px'};
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: clamp(0.95rem, 1.8vw, 1.05rem);
  line-height: 1.7;
  color: ${p => p.$light ? 'rgba(255,255,255,0.62)' : C.muted};
  margin: 0 0 ${p => p.$last ? '0' : 'clamp(1rem, 2vw, 1.35rem)'};
  text-align: ${p => p.$center ? 'center' : 'left'};
  ${p => p.$center && 'margin-left: auto; margin-right: auto;'}
`;

/* ── GROUP SAVER ── */
const SaverGrid = styled.div`
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: clamp(2.5rem, 5vw, 5rem);
  align-items: center;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const SaverCard = styled.div`
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(201,150,60,0.2);
  border-radius: 16px;
  padding: clamp(1.75rem, 3vw, 2.5rem);
`;

const SaverCardTitle = styled.h3`
  margin: 0 0 0.35rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.05rem, 2.2vw, 1.3rem);
  font-weight: 700;
  color: #fff;
`;

const SaverCardBody = styled.p`
  margin: 0 0 1.5rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.9rem;
  line-height: 1.65;
  color: rgba(255,255,255,0.55);
`;

const TierList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid rgba(201,150,60,0.15);
`;

const TierRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.85rem 1.15rem;
  border-bottom: 1px solid rgba(201,150,60,0.1);
  background: rgba(255,255,255,0.03);
  &:last-child { border-bottom: none; }
`;

const TierLabel = styled.span`
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.9rem;
  color: rgba(255,255,255,0.72);
`;

const TierBadge = styled.span`
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${C.gold};
  border: 1px solid rgba(201,150,60,0.35);
  border-radius: 999px;
  padding: 0.22rem 0.65rem;
`;

const SaverBookBtn = styled.a`
  display: inline-block;
  margin-top: 1.5rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  padding: 1rem 2.25rem;
  border-radius: 999px;
  background: ${C.gold};
  color: #fff;
  text-decoration: none;
  transition: background 0.2s ease, transform 0.18s ease;
  &:hover { background: ${C.goldDk}; transform: translateY(-1px); }
`;

const SaverPoints = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 1.85rem;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;

const SaverPoint = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: clamp(0.92rem, 1.7vw, 1rem);
  line-height: 1.55;
  color: rgba(255,255,255,0.72);
`;

const SaverCheck = styled.span`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(201,150,60,0.18);
  border: 1px solid rgba(201,150,60,0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  color: ${C.gold};
  margin-top: 0.1rem;
`;

/* ── STEPS ── */
const StepsGrid = styled.ol`
  list-style: none;
  padding: 0;
  margin: clamp(2rem, 4vw, 2.75rem) 0 0;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: clamp(1rem, 2vw, 1.25rem);
  counter-reset: step;

  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 500px) { grid-template-columns: 1fr; }
`;

const StepCard = styled.li`
  counter-increment: step;
  background: #fff;
  border: 1px solid ${C.border};
  border-radius: 14px;
  padding: clamp(1.35rem, 2.5vw, 1.75rem);
  box-shadow: 0 4px 20px rgba(16,29,64,0.06);
  position: relative;

  &::before {
    content: counter(step);
    display: block;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: ${C.navy};
    color: ${C.gold};
    font-family: 'Inter', -apple-system, sans-serif;
    font-size: 0.82rem;
    font-weight: 800;
    line-height: 34px;
    text-align: center;
    margin-bottom: 1rem;
  }
`;

const StepTitle = styled.h3`
  margin: 0 0 0.45rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1rem, 2vw, 1.15rem);
  font-weight: 700;
  color: ${C.navy};
`;

const StepBody = styled.p`
  margin: 0;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.875rem;
  line-height: 1.6;
  color: ${C.muted};
`;

/* ── EXPERIENCES ── */
const ExpGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: clamp(1rem, 2vw, 1.25rem);
  margin-top: clamp(2rem, 4vw, 2.75rem);

  @media (max-width: 1024px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 560px)  { grid-template-columns: 1fr; }
`;

const ExpCard = styled(Link)`
  display: flex;
  flex-direction: column;
  text-decoration: none;
  border-radius: 14px;
  overflow: hidden;
  background: #fff;
  border: 1px solid ${C.border};
  box-shadow: 0 4px 20px rgba(16,29,64,0.06);
  transition: transform 0.22s ease, box-shadow 0.22s ease;
  &:hover { transform: translateY(-3px); box-shadow: 0 12px 36px rgba(16,29,64,0.1); }
`;

const ExpImg = styled.div`
  height: 170px;
  overflow: hidden;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 0.35s ease;
  }
  ${ExpCard}:hover & img { transform: scale(1.04); }
`;

const ExpBody = styled.div`
  padding: 1.15rem 1.25rem 1.35rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;

const ExpAccent = styled.div`
  width: 32px;
  height: 2px;
  background: ${C.gold};
  border-radius: 2px;
  margin-bottom: 0.5rem;
`;

const ExpTitle = styled.h3`
  margin: 0;
  font-family: ${p => p.theme.fonts.main};
  font-size: 1.05rem;
  font-weight: 700;
  color: ${C.navy};
`;

const ExpText = styled.p`
  margin: 0;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.83rem;
  line-height: 1.55;
  color: ${C.muted};
`;

/* ── INFO GRID ── */
const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1px;
  background: ${C.border};
  border-radius: 10px;
  overflow: hidden;
  margin-top: clamp(2rem, 4vw, 2.75rem);

  @media (max-width: 700px) { grid-template-columns: 1fr; }
`;

const InfoItem = styled.div`
  background: #fff;
  padding: clamp(1.25rem, 2.5vw, 1.75rem);
`;

const InfoLabel = styled.div`
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(16,29,64,0.38);
  margin-bottom: 0.4rem;
`;

const InfoValue = styled.div`
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1rem, 2vw, 1.15rem);
  font-weight: 700;
  color: ${C.navy};
  line-height: 1.25;
`;

const InfoNote = styled.p`
  margin: 0.35rem 0 0;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.78rem;
  color: ${C.muted};
  line-height: 1.5;
`;

/* ── TICKET CTA BANNER ── */
const TicketBanner = styled.section`
  background: ${C.midnight};
  padding: clamp(3.5rem, 7vw, 5.5rem) clamp(1.25rem, 5vw, 3.5rem);
  text-align: center;
`;

const TicketBannerInner = styled.div`
  max-width: 640px;
  margin: 0 auto;
`;

const TicketBannerTitle = styled.h2`
  margin: 0 0 1rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(2rem, 5vw, 3.25rem);
  font-weight: 700;
  color: #fff;
  line-height: 1.08;
  em { font-style: italic; color: ${C.gold}; }
`;

const TicketBannerBody = styled.p`
  margin: 0 0 2rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: clamp(0.95rem, 1.8vw, 1.05rem);
  color: rgba(255,255,255,0.62);
  line-height: 1.65;
`;

const TicketBannerBtnRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: center;
`;

const TicketBannerPrimary = styled.a`
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.88rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  padding: 1.15rem 2.75rem;
  border-radius: 999px;
  background: ${C.gold};
  color: #fff;
  text-decoration: none;
  transition: background 0.2s ease, transform 0.18s ease;
  &:hover { background: ${C.goldDk}; transform: translateY(-1px); }
`;

const TicketBannerSecondary = styled(Link)`
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.88rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 1.15rem 2.25rem;
  border-radius: 999px;
  border: 1.5px solid rgba(255,255,255,0.35);
  background: transparent;
  color: #fff;
  text-decoration: none;
  transition: border-color 0.2s ease, background 0.2s ease;
  &:hover { border-color: rgba(255,255,255,0.6); background: rgba(255,255,255,0.06); }
`;

/* ── FAQ PREVIEW ── */
const FaqPreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: clamp(0.75rem, 2vw, 1rem);
  margin-top: clamp(2rem, 4vw, 2.75rem);

  @media (max-width: 700px) { grid-template-columns: 1fr; }
`;

const FaqPreviewItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem 1.15rem;
  background: #fff;
  border: 1px solid ${C.border};
  border-radius: 10px;
  box-shadow: 0 2px 12px rgba(16,29,64,0.04);
`;

const FaqPreviewDot = styled.span`
  flex-shrink: 0;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${C.gold};
  margin-top: 0.45rem;
`;

const FaqPreviewText = styled.p`
  margin: 0;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.875rem;
  line-height: 1.5;
  color: ${C.navy};
  font-weight: 500;
`;

const FaqCtaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: clamp(1.75rem, 3vw, 2.25rem);
`;

const FaqPrimaryBtn = styled(Link)`
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  padding: 1rem 2.25rem;
  border-radius: 999px;
  background: ${C.gold};
  color: #fff;
  text-decoration: none;
  transition: background 0.2s ease, transform 0.18s ease;
  &:hover { background: ${C.goldDk}; transform: translateY(-1px); }
`;

/* ── COMPONENT ── */
const YourFestiveVisit = () => {
  const { isActive: isBlackFriday } = useBlackFriday();

  return (
    <Page>
      <Helmet>
        <title>Your Festive Visit | Christmas 2026 at Belvoir Castle</title>
        <meta name="description" content="Plan your perfect Christmas visit to Belvoir Castle. Learn how the Group Saver works, what's included, and how to build your day — then head to the Tickets page to book." />
        <link rel="canonical" href="https://www.belvoircastle.com/christmas/your-festive-visit" />
        <meta property="og:title" content="Your Festive Visit | Christmas 2026 at Belvoir Castle" />
        <meta property="og:description" content="Plan your perfect Christmas visit to Belvoir Castle. Group Saver, how to book, and everything included in your visit." />
        <meta property="og:image" content={festive} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.belvoircastle.com/christmas/your-festive-visit" />
        <meta property="og:site_name" content="Belvoir Castle" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Your Festive Visit | Christmas 2026 at Belvoir Castle" />
        <meta name="twitter:description" content="Plan your perfect Christmas 2026 visit to Belvoir Castle." />
        <meta name="twitter:image" content={festive} />
        <meta name="twitter:site" content="@belvoircastle" />
      </Helmet>

      {/* ── HERO ── */}
      <HeroWrap>
        <HeroImg src={festive} alt="Deck the Halls at Belvoir Castle" />
        <HeroOverlay />
        <HeroContent>
          <HeroKicker>Belvoir Castle · Christmas 2026</HeroKicker>
          <HeroTitle>
            Plan Your <em>Festive Visit</em>
          </HeroTitle>
          <HeroStrap>
            Everything you need to build the perfect Christmas day at Belvoir Castle
          </HeroStrap>
          <HeroBtnRow>
            <HeroPrimaryBtn href={BOOKING_URL}>Book Now</HeroPrimaryBtn>
            <HeroSecondaryBtn to="/christmas/tickets">See All Tickets</HeroSecondaryBtn>
          </HeroBtnRow>
        </HeroContent>
      </HeroWrap>

      {/* ── INTRO (light buffer) ── */}
      <IntroStrip>
        <IntroStripInner>
          <div>
            <Kicker>Your Visit</Kicker>
            <Divider />
            <SectionTitle>
              Everything you need <em>before you book</em>
            </SectionTitle>
            <IntroStripLead>
              This page walks you through how to build your Christmas day — from choosing your date and group size to picking the experiences that make it magical. When you're ready, head to the Tickets page for full pricing and availability.
            </IntroStripLead>
            <IntroStripLink to="/christmas/tickets">View all tickets & prices →</IntroStripLink>
          </div>
          <IntroQuickGrid>
            <IntroQuickItem>
              <IntroQuickLabel>Opening Dates</IntroQuickLabel>
              <IntroQuickValue>14 Nov – 30 Dec 2026</IntroQuickValue>
            </IntroQuickItem>
            <IntroQuickItem>
              <IntroQuickLabel>Opening Hours</IntroQuickLabel>
              <IntroQuickValue>10am – 5pm daily</IntroQuickValue>
            </IntroQuickItem>
            <IntroQuickItem>
              <IntroQuickLabel>Mice Trail</IntroQuickLabel>
              <IntroQuickValue>Included with Entry</IntroQuickValue>
            </IntroQuickItem>
            <IntroQuickItem>
              <IntroQuickLabel>Pre-booking</IntroQuickLabel>
              <IntroQuickValue>Strongly recommended</IntroQuickValue>
            </IntroQuickItem>
          </IntroQuickGrid>
        </IntroStripInner>
      </IntroStrip>

      {/* ── GROUP SAVER ── */}
      <Section $bg={C.midnight} $border>
        <Inner>
          <SaverGrid>
            <div>
              <Kicker>Group Saver</Kicker>
              <Divider />
              <SectionTitle $light>
                The more you bring,<br /><em>the more you save</em>
              </SectionTitle>
              <SectionLead $light>
                Our Group Saver is designed so that Christmas at Belvoir Castle truly welcomes everyone — partners, children, grandparents, friends. As you add people to your group, your combined price updates automatically with increasing savings.
              </SectionLead>
              <SaverPoints>
                <SaverPoint>
                  <SaverCheck>✓</SaverCheck>
                  Savings stack automatically as you add more people — no codes needed
                </SaverPoint>
                <SaverPoint>
                  <SaverCheck>✓</SaverCheck>
                  Works for any combination — couples, families, groups of friends
                </SaverPoint>
                <SaverPoint>
                  <SaverCheck>✓</SaverCheck>
                  The bigger the group, the greater the saving
                </SaverPoint>
                {isBlackFriday && (
                  <SaverPoint>
                    <SaverCheck>✓</SaverCheck>
                    Black Friday unlocks an additional 20% off Entry and Santa Tickets — stacks on top of Group Saver
                  </SaverPoint>
                )}
              </SaverPoints>
              <SaverBookBtn href={BOOKING_URL}>Start Building Your Group</SaverBookBtn>
            </div>

            <SaverCard>
              <SaverCardTitle>How the savings work</SaverCardTitle>
              <SaverCardBody>Add people to your booking and watch the savings apply automatically in the booking journey.</SaverCardBody>
              <TierList>
                <TierRow>
                  <TierLabel>A couple or pair</TierLabel>
                  <TierBadge>Save more</TierBadge>
                </TierRow>
                <TierRow>
                  <TierLabel>A family of four</TierLabel>
                  <TierBadge>Save more</TierBadge>
                </TierRow>
                <TierRow>
                  <TierLabel>A group of six or more</TierLabel>
                  <TierBadge>Best saving</TierBadge>
                </TierRow>
              </TierList>
            </SaverCard>
          </SaverGrid>
        </Inner>
      </Section>

      {/* ── HOW TO BUILD YOUR VISIT ── */}
      <Section $bg={C.ivory} $border>
        <Inner>
          <Kicker $center>Step by Step</Kicker>
          <Divider $center />
          <SectionTitle $center>
            How to Build Your <em>Christmas Package</em>
          </SectionTitle>
          <SectionLead $center $wide $last>
            Everything starts with an Entry Ticket. From there, choose the add-ons that make your day magical — all booked together in one simple journey.
          </SectionLead>

          <StepsGrid>
            <StepCard>
              <StepTitle>Choose your date</StepTitle>
              <StepBody>Pick a day between 14 November and 30 December 2026. The Castle is open 10am – 5pm daily.</StepBody>
            </StepCard>
            <StepCard>
              <StepTitle>Add your group</StepTitle>
              <StepBody>Add everyone coming — adults, children, grandparents. Group Saver savings apply automatically as you build.</StepBody>
            </StepCard>
            <StepCard>
              <StepTitle>Pick your extras</StepTitle>
              <StepBody>Add Santa's Grotto, Search for Santa, Festive Afternoon Tea or Castle by Candlelight to your booking.</StepBody>
            </StepCard>
            <StepCard>
              <StepTitle>Confirm and arrive</StepTitle>
              <StepBody>You'll receive a booking confirmation with your timeslots. Arrive and enjoy Deck the Halls at its finest.</StepBody>
            </StepCard>
          </StepsGrid>
        </Inner>
      </Section>

      {/* ── EXPERIENCES ── */}
      <Section $bg="#fff" $border>
        <Inner>
          <Kicker>What's Available</Kicker>
          <Divider />
          <SectionTitle>
            Experiences to <em>Add to Your Visit</em>
          </SectionTitle>
          <SectionLead $last>
            Your Entry Ticket includes full Castle access and the Mice Trail. Explore the add-ons below — head to the Tickets page for full pricing and availability.
          </SectionLead>

          <ExpGrid>
            <ExpCard to="/christmas/santas-grotto-options">
              <ExpImg><img src={grotto} alt="Santa's Grotto" /></ExpImg>
              <ExpBody>
                <ExpAccent />
                <ExpTitle>Santa's Grotto</ExpTitle>
                <ExpText>Two grottos to choose from — inside the Castle or at the Retail Village. A truly magical encounter with Father Christmas.</ExpText>
              </ExpBody>
            </ExpCard>
            <ExpCard to="/christmas/search-for-santa">
              <ExpImg><img src={search} alt="Search for Santa" /></ExpImg>
              <ExpBody>
                <ExpAccent />
                <ExpTitle>Search for Santa</ExpTitle>
                <ExpText>An exclusive late-afternoon family trail through the Castle's Deck the Halls rooms to find Santa. Weekends only.</ExpText>
              </ExpBody>
            </ExpCard>
            <ExpCard to="/christmas/festive-afternoon-tea">
              <ExpImg><img src={tea} alt="Festive Afternoon Tea" /></ExpImg>
              <ExpBody>
                <ExpAccent />
                <ExpTitle>Festive Afternoon Tea</ExpTitle>
                <ExpText>Seasonal Christmas treats in the elegant Castle tearooms — the perfect way to round off your festive day.</ExpText>
              </ExpBody>
            </ExpCard>
            <ExpCard to="/christmas/castle-by-candlelight">
              <ExpImg><img src={festive} alt="Castle by Candlelight" /></ExpImg>
              <ExpBody>
                <ExpAccent />
                <ExpTitle>Castle by Candlelight</ExpTitle>
                <ExpText>An evening experience on select Thursdays in December — live musicians, bubbles and mince pie on arrival.</ExpText>
              </ExpBody>
            </ExpCard>
          </ExpGrid>
        </Inner>
      </Section>

      {/* ── PRACTICAL INFO ── */}
      <Section $bg={C.ivory} $border>
        <Inner>
          <Kicker>Plan Your Day</Kicker>
          <Divider />
          <SectionTitle>Good to Know</SectionTitle>
          <SectionLead $last>Everything you need before you arrive.</SectionLead>

          <InfoGrid>
            <InfoItem>
              <InfoLabel>Opening Dates</InfoLabel>
              <InfoValue>14 Nov – 30 Dec 2026</InfoValue>
              <InfoNote>Open every day including weekdays</InfoNote>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Opening Hours</InfoLabel>
              <InfoValue>10am – 5pm daily</InfoValue>
              <InfoNote>Last entry 4pm. Candlelight evenings are separate</InfoNote>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Mice Trail</InfoLabel>
              <InfoValue>Included with Entry</InfoValue>
              <InfoNote>No extra booking required</InfoNote>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Group Saver</InfoLabel>
              <InfoValue>Savings apply automatically</InfoValue>
              <InfoNote>The bigger the group, the greater the saving</InfoNote>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Parking</InfoLabel>
              <InfoValue>On-site, £4 all day</InfoValue>
              <InfoNote>Belvoir Castle, Grantham, NG32 1PA</InfoNote>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Pre-booking</InfoLabel>
              <InfoValue>Strongly recommended</InfoValue>
              <InfoNote>Online prices are better than ticket office prices</InfoNote>
            </InfoItem>
          </InfoGrid>
        </Inner>
      </Section>

      {/* ── FAQs ── */}
      <Section $bg="#fff" $border>
        <Inner>
          <Kicker>Common Questions</Kicker>
          <Divider />
          <SectionTitle>
            Christmas Visit <em>FAQs</em>
          </SectionTitle>
          <SectionLead $last>
            Booking, Group Saver, what's included, opening times and more — we've answered the questions we hear most often. Can't find what you need? The full FAQ page covers everything.
          </SectionLead>

          <FaqPreviewGrid>
            <FaqPreviewItem>
              <FaqPreviewDot />
              <FaqPreviewText>How do I book my Christmas visit?</FaqPreviewText>
            </FaqPreviewItem>
            <FaqPreviewItem>
              <FaqPreviewDot />
              <FaqPreviewText>What is Group Saver and how does it work?</FaqPreviewText>
            </FaqPreviewItem>
            <FaqPreviewItem>
              <FaqPreviewDot />
              <FaqPreviewText>What's included with an Entry Ticket?</FaqPreviewText>
            </FaqPreviewItem>
            <FaqPreviewItem>
              <FaqPreviewDot />
              <FaqPreviewText>What are the opening times during Christmas?</FaqPreviewText>
            </FaqPreviewItem>
            <FaqPreviewItem>
              <FaqPreviewDot />
              <FaqPreviewText>Can I add Santa's Grotto and Afternoon Tea to my booking?</FaqPreviewText>
            </FaqPreviewItem>
            <FaqPreviewItem>
              <FaqPreviewDot />
              <FaqPreviewText>Can I change or cancel my booking?</FaqPreviewText>
            </FaqPreviewItem>
          </FaqPreviewGrid>

          <FaqCtaRow>
            <FaqPrimaryBtn to="/christmas/your-festive-visit/faqs">View All FAQs</FaqPrimaryBtn>
          </FaqCtaRow>
        </Inner>
      </Section>

      {/* ── TICKET CTA ── */}
      <TicketBanner>
        <TicketBannerInner>
          <Kicker $center>Ready to Book?</Kicker>
          <Divider $center />
          <TicketBannerTitle>
            See All Tickets &<br /><em>Prices</em>
          </TicketBannerTitle>
          <TicketBannerBody>
            View full pricing for Entry Tickets, Santa's Grotto, Search for Santa, Festive Afternoon Tea and Castle by Candlelight — all on the Christmas Tickets page.
          </TicketBannerBody>
          <TicketBannerBtnRow>
            <TicketBannerPrimary href={BOOKING_URL}>Book Now</TicketBannerPrimary>
            <TicketBannerSecondary to="/christmas/tickets">View All Tickets</TicketBannerSecondary>
          </TicketBannerBtnRow>
        </TicketBannerInner>
      </TicketBanner>

      <NewsletterSignUp tag="Christmas 2026" />
      <Footer />
    </Page>
  );
};

export default YourFestiveVisit;
